import React, { useCallback, useMemo } from 'react';

import DataTable from 'components/DataTable';
import DataTableRenderers from 'components/DataTable/DataTable.renderers';
import useLastLoaded from 'components/DataTable/useLastLoaded';
import IncompatibleComparison from 'components/IncompatibleComparison';
import { CardSkeleton } from 'components/Skeleton';
import MetricMatrixContext from 'contexts/MetricMatrixContext';
import { useRowHighlightPredicate } from 'hooks/useRowHighlighter';

import PaceCard from './PaceCard';
import usePaceMatrixContent from './usePaceMatrixContent';

// The Pace matrix drawn by DataTable (dataTable gate). Same colDefs,
// rows, totals and renderers as the AG view. A refetch keeps the last loaded
// table up under the loading overlay rather than going back to a skeleton.
const PaceMatrixDataTableView = ({
  isCompatibleComparison,
  isLoadingData,
  colDefs,
  rowData,
  totalsRowData,
  visId,
  onDrillDown,
  isCardMode,
  getActualDateRange,
  getAverageDateRange,
  getExcludedDateRangeLabels,
  paceMatrix,
  setExportHandle,
}: ReturnType<typeof usePaceMatrixContent> & {
  setExportHandle?: (handle: DataTable.ExportHandle | undefined) => void;
}) => {
  const isRowHighlighted = useRowHighlightPredicate({
    drillField: paceMatrix.groupByField,
  });
  const isRowSelected = useCallback(
    (data: DataTable.Row) => isRowHighlighted(data) === true,
    [isRowHighlighted],
  );
  const loaded = useLastLoaded({
    value: useMemo(
      () => ({ colDefs, rowData, totalsRowData }),
      [colDefs, rowData, totalsRowData],
    ),
    isLoading: isLoadingData,
  });

  // One value per onDrillDown: a new object each render would re-render
  // every cell that reads it (as the matrix's card resizes)
  const metricMatrixContextValue = useMemo(
    () => ({ onDrillDown }),
    [onDrillDown],
  );

  if (!isCompatibleComparison) {
    return <IncompatibleComparison />;
  }

  if (!loaded) {
    return <CardSkeleton />;
  }

  if (isCardMode) {
    return (
      <PaceCard
        totalsRow={loaded.totalsRowData}
        paceMatrix={paceMatrix}
        getActualDateRange={getActualDateRange}
        getAverageDateRange={getAverageDateRange}
        getExcludedDateRangeLabels={getExcludedDateRangeLabels}
      />
    );
  }

  return (
    <MetricMatrixContext.Provider value={metricMatrixContextValue}>
      <DataTable
        tableId={visId}
        testId="grid"
        columnDefs={loaded.colDefs}
        components={DataTableRenderers}
        rows={loaded.rowData}
        totalsRows={loaded.totalsRowData}
        // As the AG view's autoSizeStrategy fitCellContents
        sizing="fitContent"
        isRowSelected={isRowSelected}
        isLoading={isLoadingData}
        isHeadlessHeightEnabled
        onExportHandle={setExportHandle}
      />
    </MetricMatrixContext.Provider>
  );
};

export default PaceMatrixDataTableView;
